import { FlightImpactDto, RouteWaypointModel } from '@fe/core';
import * as turf from '@turf/turf';
import { Position } from '../../geodesy/geodesy-types';
import { ChartFeature, ChartPolygonGeometry } from '../chart/chart-props';
import { RouteProjection } from './route-projection';

export function impactToDataSet(
    projection: RouteProjection,
    impacts: FlightImpactDto[],
    planned: [number, RouteWaypointModel][]
): ChartFeature<ChartPolygonGeometry>[] {
    const data = [] as ChartFeature<ChartPolygonGeometry>[];
    for (const impact of impacts) {
        const { startPointOff, resetPointOff, startPointIdx, resetPointIdx, altitudeBase, altitudeTop } = impact;

        if (
            startPointIdx == null ||
            resetPointIdx == null ||
            altitudeBase == null ||
            altitudeTop == null ||
            startPointIdx < 0 ||
            resetPointIdx < 0 ||
            startPointIdx >= planned.length ||
            resetPointIdx >= planned.length
        ) {
            console.warn('Invalid impact record:', impact);
            continue;
        }

        const startPoint = findPointAtOffset(startPointIdx, startPointOff, planned);
        const endPoint = findPointAtOffset(resetPointIdx, resetPointOff, planned);

        const startDist = projection.project(startPoint);
        const endDist = projection.project(endPoint);

        // Sometimes start is bigger than end... so use Math.min/max
        const x1 = Math.min(startDist, endDist);
        const y1 = Math.round(altitudeBase / 100);
        const x2 = Math.max(startDist, endDist);
        const y2 = Math.round(altitudeTop / 100);

        data.push({
            id: impact.id,
            geometry: {
                type: 'Polygon',
                coordinates: [
                    [x1, y1],
                    [x2, y1],
                    [x2, y2],
                    [x1, y2],
                    [x1, y1]
                ]
            },
            properties: impact
        });
    }
    return data;
}

function findPointAtOffset(startIdx: number, targetOffset: number, route: [number, RouteWaypointModel][]): Position {
    let currentPoint = route[startIdx][1].position;
    let cumOffset = 0;
    for (let i = startIdx; i < route.length; i++) {
        if (i + 1 >= route.length) {
            break;
        }

        const nextPoint = route[i + 1][1].position;
        const distToNext = turf.distance(currentPoint, nextPoint, { units: 'nauticalmiles' });
        if (cumOffset + distToNext < targetOffset) {
            currentPoint = nextPoint;
            cumOffset += distToNext;
            continue;
        }

        const missingDistance = targetOffset - cumOffset;

        const bearing = turf.bearing(currentPoint, nextPoint);
        currentPoint = turf.destination(currentPoint, missingDistance, bearing, { units: 'nauticalmiles' }).geometry
            .coordinates as Position;
        break;
    }

    return currentPoint;
}
